var Cortona3DSolo = require('solo');

var app = require('app').app;
var util = require('lib/util'),
    expand = util.expand,
    isFunction = util.isFunction,
    isNumber = util.isNumber;
var b64toBlob = Cortona3DSolo.app.util.b64toBlob;
var deface = require('lib/deface');

require('@cortona3d/solo-core/src/res/tiramisu.css');
require('tiramisu.css');

var m_runtimeReady = false;

Cortona3DSolo.core = expand(Cortona3DSolo.core || {}, Cortona3DSolo.config || {});

/**
 * The namespace corresponds to the Emscripten {@link external:Module Module object} of core application.
 * 
 * Requires the use of the internal module `"core"`.
 * 
 * @namespace
 * @memberof module:solo
 * @see {@link external:Module Module object}
 * @example
Cortona3DSolo.use('core', {
    features: Cortona3DSolo.app.DISABLE_DISCARDABLE_GEOMETRY_DATA,
    src: 'sample.wrl'
});
Cortona3DSolo.app.initialize();
 */
var core = Cortona3DSolo.core = expand({
    /**
     * The hook method is called when core application prints to standard error.
     * 
     * @memberof module:solo.core
     * @param {string} text
     * @see {@link http://kripken.github.io/emscripten-site/docs/api_reference/module.html#Module.printErr Module.printErr()}
     */
    printErr: function () {
        var text = deface(Array.prototype.slice.call(arguments).join(' '));
        console.error(text);
        Cortona3DSolo.core.lastErrorText = text;
    },

    /**
     * The hook method is called when core application prints to standard output.
     * 
     * @memberof module:solo.core
     * @param {string} text
     * @see {@link http://kripken.github.io/emscripten-site/docs/api_reference/module.html#Module.print Module.print()}
     */
    print: function (text) {
        if (text.indexOf('@EVENT ') === 0) {
            Cortona3DSolo.dispatch("core.traceEvent", text.substring(7));
        } else {
            console.log(deface(Array.prototype.slice.call(arguments).join(' ')));
        }
    },

    /**
     * The HTML `<canvas>` element.
     * 
     * @static
     * @member {HTMLCanvasElement} canvas
     * @memberof module:solo.core
     * @see external:HTMLCanvasElement
     */

    /**
     * The property is true when the Emscripten Module is initialized.
     * 
     * @static
     * @member {boolean} runtimeReady
     * @readonly
     * @memberof module:solo.core
     * @since 1.5.6
     */

    /**
     * The hook method is called when core application sets a message in the status bar.
     * 
     * @memberof module:solo.core
     * @method
     * @param {string} text - Message text
     */
    setStatus: function (text) { },

    /**
     * The hook method is invoked when layout is changed to resize the 3D window (the `<canvas>` element).
     * 
     * *This method can be overridden if necessary.*
     * 
     * @memberof module:solo.core
     * @function didChangeLayout
     * @see {@link module:solo.app.resize app.resize()}
     * @see The corresponding event {@link event:"core.didChangeLayout" "core.didChangeLayout"}
     */
    /**
     * @event "core.didChangeLayout"
     * @see The corresponding hook method {@link module:solo.core.didChangeLayout core.didChangeLayout()}
     */
    didChangeLayout: function () {
        if (m_runtimeReady) {
            var pane = Cortona3DSolo.core.canvas,
                w = pane.clientWidth,
                h = pane.clientHeight;
            Cortona3DSolo.app.resize(w, h);
        }
    }
}, Cortona3DSolo.core);

expand(Cortona3DSolo.core, {
    /**
     * The hook method is invoked to indicate that loading operation is in progress.
     * 
     * @private 
     * @param {ProgressEvent} e XMLHttpRequest onprogress event
     */
    onprogress: function (e) {
        var total = e.lengthComputable || e.lengthComputable === undefined ? e.total : 0;
        var loaded = e.loaded;
        Cortona3DSolo.dispatch("app.onProgress", loaded, total, e.type);
    },

    /**
     * Check if the Emscripten Module is initialized.
     * 
     * @memberof module:solo.core
     * @returns {boolean}
     * @deprecated since version 1.5.6, use {@link module:solo.core.runtimeReady core.runtimeReady} instead
     */
    isRuntimeReady: function () {
        return m_runtimeReady;
    },
    /**
     * @private
     */
    onRuntimeInitialized: function () {
        m_runtimeReady = true;

        Object.defineProperty(Module, 'runtimeReady', {
            get: function () { return m_runtimeReady },
            enumerable: true
        });

        Object.defineProperty(Module, 'canvas', {
            value: Module.canvas,
            writable: false,
            enumerable: true
        });

        if (isFunction(core.didChangeLayout)) {
            window.addEventListener('resize', function () {
                Cortona3DSolo.dispatch("core.didChangeLayout");
            }, false);
        }
        if (app.ui) {
            app.ui.showCanvas(false);
        }
        Cortona3DSolo.emit("core.onRuntimeInitialized");
        Cortona3DSolo.dispatch("app.onReady");
    }
});

var DEFAULT_TOTAL_MEMORY = 64;

function resolveSceneURL(options) {
    options = options || {};
    options.features = options.features || 0;

    var DOMURL = window.URL || window.webkitURL || window,
        syntax,
        src = options.src,
        dataScript;

    if (!src) {
        var dataScripts = document.querySelectorAll("script[type^='application/solo']");
        if (dataScripts.length > 0) {
            dataScript = dataScripts[0];
            options.totalMemory = +dataScript.getAttribute('data-total-memory') || options.totalMemory || DEFAULT_TOTAL_MEMORY;
            options.features = options.features | +dataScript.getAttribute('data-features');
            src = dataScript.getAttribute('src');
            if (src) {
                options.src = src;
            } else {
                return b64toBlob(
                    Array.prototype.slice.call(dataScripts).map(function (dataScript) { return dataScript.firstChild }),
                    'application/zip'
                )
                    .then(function (blob) {
                        options.src = DOMURL.createObjectURL(blob);
                        dataScript.parentNode.removeChild(dataScript);
                        return options;
                    });
            }
        }
    } else {
        dataScript = document.querySelector("script[type='model/vrml']");
        if (dataScript) {
            src = dataScript.getAttribute('src');
            if (src) {
                options.src = src;
            } else {
                syntax = dataScript.textContent.replace(/^\s+/, '');
                if (syntax.indexOf('#VRML V2.0 utf8') !== 0) {
                    syntax = '#VRML V2.0 utf8\n' + syntax;
                }
                options.isRAContent = /PROTO\s+ObjectVM/.test(syntax);
                options.isViewpointInScene = /Viewpoint\s*{/.test(syntax);
                options.isTextInScene = /Text\s*{/.test(syntax);
                options.src = DOMURL.createObjectURL(new Blob([syntax], {
                    type: "model/vrml"
                }));
            }
            options.totalMemory = +dataScript.getAttribute('data-total-memory') || options.totalMemory || DEFAULT_TOTAL_MEMORY;
            options.features = options.features | +dataScript.getAttribute('data-features');
            if (!options.isRAContent) {
                options.features |= +Cortona3DSolo.app.DISABLE_DISCARDABLE_GEOMETRY_DATA;
            }
            if (options.isTextInScene) {
                options.features |= +Cortona3DSolo.app.ENABLE_VRML_TEXT_NODE;
            }
            dataScript.parentNode.removeChild(dataScript);
        }
    }
    return Promise.resolve(options);
}

module.exports = {
    configure: function (options) {
        // Module configuration
        options = options || {};
        var config = {},
            canvasElement;

        if (options.prefixURL) {
            Cortona3DSolo.baseUrl = options.prefixURL;
        }

        canvasElement = options.element || options.canvas || (function () {
            var canvas = document.createElement('canvas');
            require('core.css');
            document.body.insertBefore(canvas, document.body.firstChild);
            return canvas;
        })();

        canvasElement.classList.add('cortona3dsolo-canvas');

        if (!canvasElement.id) {
            canvasElement.id = 'canvas';
        }

        var w = canvasElement.clientWidth,
            h = canvasElement.clientHeight;

        canvasElement.width = w;
        canvasElement.height = h;

        var coreSolo = require('core.solo'),
            moduleArguments = coreSolo.Arguments;

        moduleArguments.canvas = '#' + canvasElement.id;

        Module.canvas = canvasElement;
        Module.runtimeReady = m_runtimeReady;

        resolveSceneURL(options)
            .then(function (options) {
                if (!Cortona3DSolo.core.locateFile) {
                    config.locateFile = function (url) {
                        var path = Cortona3DSolo.baseUrl + url;
                        if (/\.wasm$/i.test(url) || /Cortona3DSoloCore.*\.js$/i.test(url) || /Cortona3DSoloCore.*\.data$/i.test(url)) {
                            path += /\?/.test(path) ? '&' : '?';
                            path += SOURCEHASH;
                        }
                        return path;
                    }
                }

                if (isNumber(options.totalMemory)) {
                    config.TOTAL_MEMORY = options.totalMemory * 1024 * 1024;
                }

                if (options.features) {
                    app.configureInstance(options.features);
                }

                expand(Cortona3DSolo.core, config);

                if (options.src) {
                    moduleArguments.document = Cortona3DSolo.app.util.toUrl(options.src);
                }

                if (options.features) {
                    moduleArguments.features = options.features & 32767;
                }

                moduleArguments.toModule();

                coreSolo.load(options.coreURL);
            })
            .catch(function (e) {
                Cortona3DSolo.emit('core.onError', e);
            });

        return Module;
    }
};

/**
 *  The hook method is invoked when animation frame is drawn on the canvas.
 *
 * @memberof module:solo.core
 * @abstract
 * @function didDrawAnimationFrame
 * @since 1.4.0
 * @see The corresponding event {@link event:"core.didDrawAnimationFrame" "core.didDrawAnimationFrame"}
 */
/**
 * @event "core.didDrawAnimationFrame"
 * @see The corresponding hook method {@link module:solo.core.didDrawAnimationFrame core.didDrawAnimationFrame()}
 */
//didDrawAnimationFrame: nop, // ()